import { useSubscriptionsStore } from "@/stores/subscriptions";
import {
  premiumLCNs,
  addOnLCNs,
  nonAllstateWebrootLCNs,
  allstateLCNs,
  allstatePremiumFamilyLCNs,
  webrootCDLPcodes,
} from "@/common/webrootProductName";
import { useAllstateSubscriptionsStore } from "@/stores/allstateSubscriptions";
import {
  cancelled,
  SUBSCRIPTION_BESTBUY_RENEWAL_CONVERSION_DAY,
  SUBSCRIPTION_LAST_ALLSTATE_DAY,
  SUBSCRIPTION_MIN_ACTIVE_DAY,
  SUBSCRIPTION_MIN_EXPIRED_DAY,
  URLS,
} from "@/define";
import { useNotificationsStore } from "@/stores/notifications";
import { unifiedApi } from "@/common";
import { getUrl } from "@/common/getUrl";
import { IAllstateResponse, ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { KeyCodeSeller, KeycodeStatus } from "@/globalEnums";

async function getSubscriptions() {
  const subscriptionsStore = useSubscriptionsStore();
  await subscriptionsStore.populateSubscriptions();
  const subscriptions = subscriptionsStore.subscriptions;
  return subscriptions;
}

async function getAllstateSubscriptions() {
  const allstateSubscriptionsStore = useAllstateSubscriptionsStore();
  const subscriptions = (await allstateSubscriptionsStore.getAllstateSubscriptions()).allstateSubscriptions;
  return subscriptions;
}

export function calculateRemainingDays(remainingDays, keycodeSellerType) {
  const allstateSubscriptionsStore = useAllstateSubscriptionsStore();
  return keycodeSellerType === KeyCodeSeller.BestBuy && allstateSubscriptionsStore.hasAccountSetup
    ? remainingDays + SUBSCRIPTION_BESTBUY_RENEWAL_CONVERSION_DAY * -1
    : remainingDays;
}

// Returns if the user has non-Allstate Webroot subscriptions
export async function hasWebrootSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        nonAllstateWebrootLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_MIN_EXPIRED_DAY
    );
  return response;
}

export async function hasActivePremiumSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        premiumLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > 0 &&
        l.webrootStatusDescription.toLowerCase() === KeycodeStatus.Active
    );
  return response;
}

export async function hasActivePremiumFamilySubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        allstatePremiumFamilyLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > 0 &&
        l.webrootStatusDescription.toLowerCase() === KeycodeStatus.Active
    );
  return response;
}

export async function hasActiveCDLPSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        webrootCDLPcodes.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) &&
        l.webrootStatusDescription.toLowerCase() === KeycodeStatus.Active
    );
  return response;
}

export async function hasInactivePremiumSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        premiumLCNs.includes(l.licenseCategoryName ?? "") &&
        l.webrootStatusDescription.toLowerCase() === KeycodeStatus.Inactive
    );
  return response;
}

export async function hasExpiredPremiumSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        premiumLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) < 0 &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) >= SUBSCRIPTION_LAST_ALLSTATE_DAY
    );
  return response;
}

export async function hasExpiredPremiumFamilySubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        allstatePremiumFamilyLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) < 0 &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) >= SUBSCRIPTION_LAST_ALLSTATE_DAY
    );
  return response;
}

export async function hasAddOnSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        addOnLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_MIN_EXPIRED_DAY
    );
  return response;
}

export async function hasAllstateSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        allstateLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_LAST_ALLSTATE_DAY
    );
  return response;
}

export async function hasAllstateAccountSetup() {
  const allstateSubscriptions = await getAllstateSubscriptions();
  const response = allstateSubscriptions && allstateSubscriptions.some(a => a.canceledOnDate === null);
  return response;
}

export async function hasAllstateUpgradeNotification() {
  const allstateSubscriptions = await getAllstateSubscriptions();
  const response = allstateSubscriptions && allstateSubscriptions.some(a => a.canceledOnDate === null);
  return response;
}

export function dismissAllstateUpgradeNotification() {
  const notificationsStore = useNotificationsStore();
  notificationsStore.clearNotifications();
  unifiedApi.updateAllstateSubscriptions({ showUpgradedBanner: false });
  const allstateStore = useAllstateSubscriptionsStore();
  const allstateSubs = allstateStore.allstateSubscriptions;
  allstateSubs.forEach(l => {
    l.showUpgradedBanner = false;
  });
  allstateStore.allstateSubscriptions = allstateSubs;
}

// allstate upgrade banner displays if More than 90 days from sub expiring and api returns showUpgradedBanner and flag is set to show allstate family
export async function showAllstateBanner() {
  const notificationsStore = useNotificationsStore();
  const subscriptionsStore = useSubscriptionsStore();
  const allstateStore = useAllstateSubscriptionsStore();

  if (subscriptionsStore.subscriptions.length === 0) {
    await subscriptionsStore.populateSubscriptions();
  }
  const allstateSubscription = subscriptionsStore.subscriptions.find(l =>
    allstateLCNs.includes(l.licenseCategoryName ?? "")
  );
  if (
    allstateStore.allstateSubscriptions.some(l => l.showUpgradedBanner) &&
    allstateSubscription?.remainingDays &&
    allstateSubscription?.remainingDays > SUBSCRIPTION_MIN_ACTIVE_DAY &&
    allstateSubscription.zuoraSubscriptionStatus.toLowerCase() !== cancelled
  ) {
    const lcn = allstateSubscription.licenseCategoryName;
    notificationsStore.clearNotifications();
    notificationsStore.addNotification({ type: "AllstateUpgradeInfo", params: { licenseCategoryName: lcn } });
  }
}

export async function getUpgradeCartUrl(keycode: string) {
  const url = (await unifiedApi.getWebrootRenewUrl(keycode)).data.redirectUrl;
  if (url) {
    const queryString = url.split("?")[1];
    // This URL will be only used here, so no need to add to env files.
    return `${getUrl("ALLSTATE_UPGRADE_CART")}?${queryString}`;
  }
}

export function getAllstateManageUrl() {
  return `${URLS.UP_ENDPOINT.protocol}://${URLS.UP_ENDPOINT.hostname}/api/cp/auth/allstate`;
}

export function getActiveAllState(allstateSubscriptions: IAllstateResponse[], subscriptions: ISubscription[]) {
  return allstateSubscriptions.length > 0 &&
    subscriptions.some(
      l =>
        l.keyCode === allstateSubscriptions[0].keyCode &&
        l.keyCodeSellerType === KeyCodeSeller.Webroot &&
        !allstateSubscriptions[0].canceledOnDate
    )
    ? subscriptions.find(
        l =>
          l.keyCode === allstateSubscriptions[0].keyCode &&
          l.keyCodeSellerType === KeyCodeSeller.Webroot &&
          !allstateSubscriptions[0].canceledOnDate
      )
    : subscriptions.some(
          l =>
            allstateLCNs.includes(l.licenseCategoryName ?? "") &&
            calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_MIN_EXPIRED_DAY
        )
      ? subscriptions.find(
          l =>
            allstateLCNs.includes(l.licenseCategoryName ?? "") &&
            calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_MIN_EXPIRED_DAY
        )
      : subscriptions.find(
          l =>
            allstateLCNs.includes(l.licenseCategoryName ?? "") &&
            calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) <= SUBSCRIPTION_MIN_EXPIRED_DAY
        );
}
